import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAuthDetails } from "../../Common/cookiesHelper";
import { withReactQueryBoundary } from "../../lib/react-query";
import { FORCE_NODE_API_FOR_DEAL_PRODUCT_PICKERS } from "../../Constants/Config";
import {
  useNewPurchaseOrderCategoriesQuery,
  useNewPurchaseOrderProductListInfiniteQuery,
} from "../../queries/purchaseOrders";
import { pinCategoryInList } from "../NewPurchaseOrder/apiUtils";
import {
  btnGhost,
  btnPrimary,
  btnPrimaryDisabled,
} from "../NewPurchaseOrder/createStyles";
import EllipsisTooltip from "../NewPurchaseOrder/components/EllipsisTooltip";
import Icon from "../NewInventory/Icon";
import Modal from "../NewPurchaseOrder/components/create/Modal";
import PickerFilterDropdown from "../NewPurchaseOrder/components/create/PickerFilterDropdown";
import { ToastifyAlert } from "../../CommonComponents/ToastifyAlert";

const BOGO_PRODUCT_LIST_PER_PAGE = 100;
const ZERO_PRICE_PERCENTAGE_MESSAGES = {
  bogo: "Products priced at $0.00 can't be added to a percentage BOGO deal.",
  mix_match:
    "Products priced at $0.00 can't be added to a percentage Mix & Match deal.",
};
const SCROLL_FETCH_THRESHOLD_PX = 140;

const normalizeId = (value) => String(value ?? "").trim();

const normalizeVariantId = (value) => {
  const id = normalizeId(value);
  return id && id !== "0" ? id : "";
};

const getBogoSelectionKey = (productId, variantId) =>
  `${normalizeId(productId)}::${normalizeVariantId(variantId) || "base"}`;

const getApiRowSelectionKey = (row) =>
  getBogoSelectionKey(row?.product_id, row?.varient_id);

const getBogoItemSelectionKey = (item) =>
  getBogoSelectionKey(item?.id || item?.product_id, item?.var_id);

const getRowPrice = (row) => {
  const price = Number.parseFloat(row?.price ?? "0");
  return Number.isFinite(price) ? price : 0;
};

const hasNoUpc = (row) => !String(row?.upc ?? "").trim();

const formatVariantLabel = (row, productName) => {
  const variantName = String(row?.variant_name || "").trim();
  if (variantName) return variantName;

  const rawName = String(row?.product_name || "").trim();
  if (!rawName)
    return normalizeVariantId(row?.varient_id) ? "Variant" : "Default";
  const productPrefix = `${productName} / `;
  if (rawName.startsWith(productPrefix)) {
    return rawName.slice(productPrefix.length).trim() || "Variant";
  }
  if (rawName === productName) {
    return normalizeVariantId(row?.varient_id) ? "Variant" : "Default";
  }
  return rawName;
};

const formatBogoItemTitle = (row, group) => {
  const productName = String(group?.productName || "").trim();
  const variantName = String(row?.variant_name || "").trim();
  const rawName = String(row?.product_name || "").trim();

  if (!normalizeVariantId(row?.varient_id)) {
    return productName || rawName || "Product";
  }
  if (rawName.includes("~")) return rawName;
  if (rawName.includes(" / ")) return rawName.replace(" / ", "~");
  if (productName && variantName) return `${productName}~${variantName}`;
  return rawName || productName || "Variant";
};

const buildBogoItemFromApiRow = (row, group) => {
  const productId = normalizeId(row?.product_id || group?.productId);
  const variantId = normalizeVariantId(row?.varient_id);
  const price = String(row?.price ?? "0");
  const upc = String(row?.upc || "");
  const customCode = String(row?.custom_code || "");
  const baseItem = {
    id: productId,
    product_id: productId,
    isvarient: variantId ? "1" : "0",
    title: formatBogoItemTitle(row, group),
    // Always keep selling `price` so Mix & Match / BOGO discount filters work.
    price,
    custom_code: customCode,
    is_lottery: String(row?.is_lottery ?? "0"),
  };

  if (variantId) {
    return {
      ...baseItem,
      var_id: variantId,
      var_price: price,
      var_upc: upc,
      var_custom_code: customCode,
      upc,
    };
  }

  return {
    ...baseItem,
    upc,
  };
};

const buildSelectionMap = (items = []) =>
  (Array.isArray(items) ? items : []).reduce((selectionMap, item) => {
    const productId = normalizeId(item?.id || item?.product_id);
    if (!productId) return selectionMap;
    selectionMap[getBogoItemSelectionKey(item)] = item;
    return selectionMap;
  }, {});

const PickerCheckbox = ({ state, size = 18 }) => {
  const baseStyle = {
    width: size,
    height: size,
    borderRadius: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxSizing: "border-box",
  };

  if (state === "checked") {
    return (
      <span style={{ ...baseStyle, background: "var(--qv-brand)" }} aria-hidden="true">
        <Icon name="check" size={11} color="var(--qv-brand-on, #ffffff)" />
      </span>
    );
  }

  if (state === "indeterminate") {
    return (
      <span style={{ ...baseStyle, background: "var(--qv-brand)" }} aria-hidden="true">
        <span
          style={{
            width: 8,
            height: 2,
            borderRadius: 1,
            background: "var(--qv-brand-on, #ffffff)",
          }}
        />
      </span>
    );
  }

  return (
    <span
      style={{
        ...baseStyle,
        border: "1.5px solid var(--qv-bd-cdd2da, #cdd2da)",
        background: "var(--qv-bg-ffffff, #ffffff)",
      }}
      aria-hidden="true"
    />
  );
};

const BogoProductSelectionModal = ({
  open,
  onClose,
  onConfirm,
  merchantId: merchantIdProp,
  confirmedSelections = [],
  activeTab = "amount",
  discountAmount = "0.00",
  modalError = "",
  setModalError,
  // Create: exclude_deal_products only. Edit: also current_deal_type + current_deal_id.
  excludeDealProducts = "1",
  currentDealType,
  currentDealId,
  mode,
  dealId,
  // When true, products CURRENTLY selected in the deal are hidden from the
  // picker list (they're managed via the chips outside the modal). Removing a
  // product deselects it, so it drops out of confirmedSelections and reappears
  // here — ready to be re-added. current_deal_id is still sent, so the deal's
  // own products stay candidates in the API response.
  hideSelectedProducts = false,
  // "bogo" | "mix_match" — drives exclude_deal_products current_deal_type
  dealType = "bogo",
  title,
  subtitle,
}) => {
  const isNarrow = useMediaQuery("(max-width: 600px)");
  const { userTypeData } = useAuthDetails();
  const merchantId = normalizeId(merchantIdProp);
  const resolvedDealType = dealType === "mix_match" ? "mix_match" : "bogo";
  const modalTitle =
    title ||
    (resolvedDealType === "mix_match"
      ? "Add products to Mix & Match deal"
      : "Add products to BOGO deal");
  const modalSubtitle =
    subtitle || "Pick the products and variants eligible for this offer";
  const authPayload = useMemo(
    () => ({
      token: userTypeData?.token || "",
      token_id: userTypeData?.token_id || "",
      login_type: userTypeData?.login_type || "",
      session_id: userTypeData?.session_id || "",
    }),
    [
      userTypeData?.token,
      userTypeData?.token_id,
      userTypeData?.login_type,
      userTypeData?.session_id,
    ],
  );

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [collapsed, setCollapsed] = useState(new Set());
  const [draftSelections, setDraftSelections] = useState(() =>
    buildSelectionMap(confirmedSelections),
  );
  const bodyRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;
    setDraftSelections(buildSelectionMap(confirmedSelections));
    setCollapsed(new Set());
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 60);
    return () => window.clearTimeout(timer);
  }, [confirmedSelections, open]);

  const discAmt = useMemo(() => {
    if (activeTab !== "amount") return undefined;
    const discount = Number.parseFloat(discountAmount);
    if (!Number.isFinite(discount) || discount <= 0) return undefined;
    return discount.toFixed(2);
  }, [activeTab, discountAmount]);

  const dealExcludeFilters = useMemo(() => {
    const filters = { excludeDealProducts: "1" };
    if (mode === "edit" && dealId != null && String(dealId).trim() !== "") {
      filters.currentDealType = resolvedDealType;
      filters.currentDealId = String(dealId);
    }
    return filters;
  }, [mode, dealId, resolvedDealType]);

  const productsQuery = useNewPurchaseOrderProductListInfiniteQuery({
    auth: authPayload,
    merchantId,
    vendorId: "",
    filterVendorId: "",
    categoryId,
    search: debouncedQuery,
    perPage: BOGO_PRODUCT_LIST_PER_PAGE,
    discAmt,
    excludeDealProducts,
    currentDealType,
    currentDealId,
    ...dealExcludeFilters,
    forceNodeApi: FORCE_NODE_API_FOR_DEAL_PRODUCT_PICKERS,
    enabled: open,
    // Fire the products API on every open. The default productPicker cache
    // (2-min staleTime) plus the app-wide refetchOnMount:false made the list
    // skip its network call when the modal was reopened with an unchanged
    // discount (same query key) — the fetch looked intermittent. staleTime:0
    // marks it always-stale and refetchOnMount:"always" forces the mount fetch.
    staleTime: 0,
    refetchOnMount: "always",
  });
  const fetchNextProductPage = productsQuery.fetchNextPage;

  const categoriesQuery = useNewPurchaseOrderCategoriesQuery({
    merchantId,
    enabled: open,
  });
  const categories = useMemo(() => {
    const categoryList =
      categoriesQuery.categories ?? categoriesQuery.data ?? [];
    return pinCategoryInList(categoryList, selectedCategory);
  }, [categoriesQuery.categories, categoriesQuery.data, selectedCategory]);
  const categoryItems = useMemo(
    () =>
      categories.map((category) => ({ id: category.id, label: category.name })),
    [categories],
  );
  const selectedCategoryItem = useMemo(() => {
    if (!categoryId) return null;
    const category = categories.find(
      (item) => normalizeId(item.id) === normalizeId(categoryId),
    );
    return category ? { id: category.id, label: category.name } : null;
  }, [categories, categoryId]);

  const productGroups = useMemo(
    () => productsQuery.data?.pages.flatMap((page) => page.products) ?? [],
    [productsQuery.data],
  );

  // Keys of products currently selected in the deal — hidden from the list when
  // hideSelectedProducts is on (they live in the chips; removing one there makes
  // it reappear here). Null = feature off, no hiding.
  const hiddenSelectionKeys = useMemo(() => {
    if (!hideSelectedProducts) return null;
    const keys = new Set();
    (Array.isArray(confirmedSelections) ? confirmedSelections : []).forEach(
      (item) => keys.add(getBogoItemSelectionKey(item)),
    );
    return keys;
  }, [hideSelectedProducts, confirmedSelections]);

  const visibleGroups = useMemo(() => {
    // Price filtering is already done by the API via `disc_amt` when Amount tab
    // is active. Do NOT re-filter on `row.price` here: the v2 list often returns
    // `cost_price` without selling `price`, which normalizes to 0 and incorrectly
    // hides every row ("No products match") even though the API returned data.
    // Deal-product exclusion is also handled by the API (`exclude_deal_products`).
    return productGroups
      .map((group) => {
        const visibleRows = (group?.rowItems || []).filter((row) => {
          if (String(row?.is_lottery ?? "0") === "1") return false;
          // Hide products already selected in the deal (managed via chips).
          if (hiddenSelectionKeys?.has(getApiRowSelectionKey(row))) return false;
          return true;
        });
        return { group, visibleRows };
      })
      .filter(({ visibleRows }) => visibleRows.length > 0);
  }, [productGroups, hiddenSelectionKeys]);

  const loadingInitial = productsQuery.isLoading;
  const loadingMore = productsQuery.isFetchingNextPage;
  const listRefreshing =
    productsQuery.isFetching && !loadingInitial && !loadingMore;
  const loadError = productsQuery.isError
    ? productsQuery.error?.message || "Failed to fetch products."
    : "";
  // v2 can return HTTP 200 with status:false + a guidance message (e.g. the
  // search matched too many products). Surface it in place of the empty list.
  const refineSearchMessage = useMemo(() => {
    const pages = productsQuery.data?.pages ?? [];
    for (const page of pages) {
      if (page?.meta?.message) return page.meta.message;
    }
    return "";
  }, [productsQuery.data]);
  const hasMore = Boolean(productsQuery.hasNextPage);
  const categoriesLoading =
    open &&
    !categoriesQuery.isFetched &&
    (categoriesQuery.isLoading || categoriesQuery.isFetching);
  const categoriesListLoading = Boolean(
    open && categoriesQuery.isFetching && !categoriesQuery.isFetchingNextPage,
  );

  const loadNextPage = useCallback(() => {
    if (!open || loadingInitial || loadingMore || listRefreshing || !hasMore)
      return;
    fetchNextProductPage();
  }, [
    hasMore,
    listRefreshing,
    loadingInitial,
    loadingMore,
    open,
    fetchNextProductPage,
  ]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [categoryId, debouncedQuery]);

  const isSelected = (row) =>
    Boolean(draftSelections[getApiRowSelectionKey(row)]);

  // A percentage discount (BOGO or Mix & Match) means nothing on a $0.00
  // item, so those rows stay visible but can't be newly selected.
  // Already-selected rows can still be removed.
  const blockZeroPriced = activeTab === "percentage";
  const zeroPriceMessage = ZERO_PRICE_PERCENTAGE_MESSAGES[resolvedDealType];
  const isZeroPriceLocked = (row) =>
    blockZeroPriced && getRowPrice(row) <= 0 && !isSelected(row);

  const toggleRow = (row, group) => {
    const key = getApiRowSelectionKey(row);
    if (listRefreshing || (hasNoUpc(row) && !draftSelections[key])) return;
    if (isZeroPriceLocked(row)) {
      ToastifyAlert(zeroPriceMessage, "error");
      return;
    }
    setModalError?.("");
    setDraftSelections((previousSelections) => {
      const nextSelections = { ...previousSelections };
      if (nextSelections[key]) {
        delete nextSelections[key];
      } else {
        nextSelections[key] = buildBogoItemFromApiRow(row, group);
      }
      return nextSelections;
    });
  };

  const toggleProduct = (productId) => {
    setCollapsed((previousCollapsed) => {
      const nextCollapsed = new Set(previousCollapsed);
      if (nextCollapsed.has(productId)) nextCollapsed.delete(productId);
      else nextCollapsed.add(productId);
      return nextCollapsed;
    });
  };

  const toggleAllForProduct = (group, visibleRows) => {
    if (listRefreshing) return;
    const selectableRows = visibleRows.filter(
      (row) => !hasNoUpc(row) && !isZeroPriceLocked(row),
    );
    if (selectableRows.length === 0) {
      if (visibleRows.some(isZeroPriceLocked)) {
        ToastifyAlert(zeroPriceMessage, "error");
      }
      return;
    }
    const allSelected = selectableRows.every(isSelected);
    setModalError?.("");
    setDraftSelections((previousSelections) => {
      const nextSelections = { ...previousSelections };
      selectableRows.forEach((row) => {
        const key = getApiRowSelectionKey(row);
        if (allSelected) delete nextSelections[key];
        else nextSelections[key] = buildBogoItemFromApiRow(row, group);
      });
      return nextSelections;
    });
  };

  const resetPickerState = () => {
    setQuery("");
    setDebouncedQuery("");
    setCategoryId("");
    setSelectedCategory(null);
    setCollapsed(new Set());
  };

  const handleClose = () => {
    resetPickerState();
    setDraftSelections(buildSelectionMap(confirmedSelections));
    setModalError?.("");
    onClose?.();
  };

  const handleConfirm = () => {
    if (listRefreshing) return;
    const selections = Object.values(draftSelections);
    if (selections.length === 0) {
      setModalError?.("Please select at least 1 product variant.");
      return;
    }
    onConfirm?.(selections);
    resetPickerState();
  };

  const handleBodyScroll = (event) => {
    const body = event.currentTarget;
    const nearBottom =
      body.scrollHeight - (body.scrollTop + body.clientHeight) <=
      SCROLL_FETCH_THRESHOLD_PX;
    if (nearBottom) loadNextPage();
  };

  const selectionCount = Object.keys(draftSelections).length;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      width={760}
      ariaLabel={modalTitle}
      closeOnBackdrop={false}
    >
      <div
        className={`qv-promo-product-picker qv-promo-product-picker--${resolvedDealType === "mix_match" ? "mix-match" : "bogo"}`}
        style={{
          display: "flex",
          flexDirection: "column",
          height: "80vh",
          maxHeight: 700,
          minHeight: 0,
        }}
      >
        <header
          className="qv-promo-product-picker__header"
          style={{
            padding: "20px 24px 12px",
            borderBottom: "1px solid var(--qv-bd-eef0f3, #eef0f3)",
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--qv-tx-7a818f, #7a818f)",
            }}
          >
            <Icon name="close" size={14} />
          </button>
          <div style={{ fontSize: 17, fontWeight: 500, color: "var(--qv-tx-0f1115, #0f1115)" }}>
            {modalTitle}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--qv-tx-7a818f, #7a818f)", marginTop: 3 }}>
            {modalSubtitle}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 12,
              flexWrap: "wrap",
            }}
          >
            <PickerFilterDropdown
              value={categoryId}
              onChange={(id) => {
                if (!id) {
                  setCategoryId("");
                  setSelectedCategory(null);
                  return;
                }
                const category = categories.find(
                  (item) => normalizeId(item.id) === normalizeId(id),
                );
                setCategoryId(normalizeId(id));
                setSelectedCategory(category || null);
              }}
              selectedItem={selectedCategoryItem}
              items={categoryItems}
              allLabel="All categories"
              searchPlaceholder="Search categories"
              icon="category"
              loading={categoriesLoading}
              loadingLabel="Loading categories..."
              listLoading={categoriesListLoading}
              onLoadMore={categoriesQuery.loadMore}
              hasMore={Boolean(categoriesQuery.hasMore)}
              loadingMore={Boolean(categoriesQuery.isFetchingNextPage)}
              menuMaxHeight="min(320px, max(80px, calc(100dvh - 300px)))"
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 12,
              flexWrap: "wrap",
            }}
          >
            <div
              className="qv-promo-product-picker__search"
              style={{
                flex: 1,
                minWidth: 200,
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "0 12px",
                height: 36,
                background: "var(--qv-bg-ffffff, #ffffff)",
                border: "1px solid var(--qv-bd-d0d4dc, #d0d4dc)",
                borderRadius: 8,
                color: "var(--qv-tx-7a818f, #7a818f)",
              }}
            >
              <Icon name="search" size={14} />
              <input
                ref={searchInputRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search product, variant, UPC, or code"
                aria-label="Search products"
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 13,
                  color: "var(--qv-tx-0f1115, #0f1115)",
                  fontFamily: "inherit",
                }}
              />
            </div>
            {listRefreshing ? (
              <span
                style={{ fontSize: 12, color: "var(--qv-tx-7a818f, #7a818f)", whiteSpace: "nowrap" }}
              >
                Updating list…
              </span>
            ) : null}
          </div>
        </header>

        <main
          ref={bodyRef}
          className="qv-promo-product-picker__body"
          onScroll={handleBodyScroll}
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "8px 24px",
            minHeight: 0,
          }}
        >
          {loadingInitial ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                fontSize: 13,
                color: "var(--qv-tx-7a818f, #7a818f)",
              }}
            >
              Loading products...
            </div>
          ) : loadError ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                fontSize: 13,
                color: "var(--qv-tx-d32f2f, #d32f2f)",
              }}
            >
              {loadError}
            </div>
          ) : refineSearchMessage ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                fontSize: 13,
                color: "var(--qv-tx-b45309, #b45309)",
                fontWeight: 500,
              }}
            >
              {refineSearchMessage}
            </div>
          ) : visibleGroups.length === 0 && !loadingMore ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                fontSize: 13,
                color: "var(--qv-tx-7a818f, #7a818f)",
              }}
            >
              No products match.
            </div>
          ) : (
            visibleGroups.map(({ group, visibleRows }) => {
              const hasVariants = group.hasVariants && visibleRows.length > 0;
              const isExpanded = hasVariants && !collapsed.has(group.productId);
              const baseRow = !hasVariants ? visibleRows[0] : null;
              const selectableRows = visibleRows.filter(
                (row) => !hasNoUpc(row) && !isZeroPriceLocked(row),
              );
              const hasZeroPriceLockedRows =
                visibleRows.some(isZeroPriceLocked);
              const baseRowNoUpc = Boolean(baseRow && hasNoUpc(baseRow));
              const baseRowLocked = Boolean(
                baseRow &&
                  !isSelected(baseRow) &&
                  (baseRowNoUpc || isZeroPriceLocked(baseRow)),
              );
              const parentCheckboxDisabled =
                listRefreshing ||
                (selectableRows.length === 0 && !hasZeroPriceLockedRows);
              const selectedCount = visibleRows.filter(isSelected).length;
              const allSelected =
                selectableRows.length > 0 && selectableRows.every(isSelected);
              const someSelected = selectedCount > 0 && !allSelected;
              const parentCheckboxState = allSelected
                ? "checked"
                : someSelected
                  ? "indeterminate"
                  : "unchecked";
              const variantsCount = visibleRows.length;

              return (
                <section
                  className="qv-promo-product-picker__group"
                  key={group.productId}
                  style={{
                    borderBottom: "1px solid var(--qv-bd-f2f4f7, #f2f4f7)",
                    padding: "8px 0",
                  }}
                >
                  <div
                    className="qv-promo-product-picker__product-row"
                    onClick={() => {
                      if (hasVariants) toggleProduct(group.productId);
                      else if (baseRow) toggleRow(baseRow, group);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      if (hasVariants) toggleProduct(group.productId);
                      else if (baseRow) toggleRow(baseRow, group);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-expanded={hasVariants ? isExpanded : undefined}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "6px 0",
                      cursor:
                        listRefreshing || baseRowLocked
                          ? "not-allowed"
                          : "pointer",
                      minWidth: 0,
                      overflow: "hidden",
                      opacity: listRefreshing || baseRowLocked ? 0.6 : 1,
                    }}
                  >
                    <button
                      type="button"
                      disabled={parentCheckboxDisabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleAllForProduct(group, visibleRows);
                      }}
                      onKeyDown={(event) => event.stopPropagation()}
                      title={allSelected ? "Deselect all" : "Select all"}
                      style={{
                        cursor:
                          parentCheckboxDisabled || selectableRows.length === 0
                            ? "not-allowed"
                            : "pointer",
                        display: "flex",
                        padding: 0,
                        border: 0,
                        background: "transparent",
                      }}
                    >
                      <PickerCheckbox state={parentCheckboxState} />
                    </button>
                    {hasVariants ? (
                      <Icon
                        name={isExpanded ? "chevron-down" : "chevron-right"}
                        size={14}
                        color="var(--qv-tx-7a818f, #7a818f)"
                      />
                    ) : (
                      <span style={{ width: 14, flexShrink: 0 }} />
                    )}
                    <span
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: "var(--qv-bg-f4f6f9, #f4f6f9)",
                        color: "var(--qv-tx-7a818f, #7a818f)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon name="box" size={14} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                      <EllipsisTooltip
                        title={group.productName}
                        measureKey={`bogo-picker-group-${group.productId}`}
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          fontFamily: "CircularMedium",
                          color: "var(--qv-tx-0f1115, #0f1115)",
                        }}
                      >
                        {group.productName}
                      </EllipsisTooltip>
                      <span
                        style={{
                          display: "block",
                          fontSize: 11.5,
                          color: "var(--qv-tx-7a818f, #7a818f)",
                          marginTop: 1,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {hasVariants
                          ? `${variantsCount} variant${variantsCount === 1 ? "" : "s"}`
                          : baseRow
                            ? `${baseRow.upc || "-"} · $ ${getRowPrice(baseRow).toFixed(2)}`
                            : ""}
                      </span>
                    </span>
                    {selectedCount > 0 ? (
                      <span
                        style={{
                          fontSize: 11,
                          padding: "3px 8px",
                          borderRadius: 99,
                          background: "var(--qv-brand-soft)",
                          color: "var(--qv-brand-text)",
                          fontWeight: 500,
                          fontFamily: "CircularMedium",
                          flexShrink: 0,
                        }}
                      >
                        {selectedCount} selected
                      </span>
                    ) : baseRowNoUpc ? (
                      <span
                        style={{
                          fontSize: 11,
                          padding: "3px 8px",
                          borderRadius: 99,
                          background: "var(--qv-bg-fdecea, #fdecea)",
                          color: "var(--qv-tx-d32f2f, #d32f2f)",
                          fontWeight: 500,
                          fontFamily: "CircularMedium",
                          flexShrink: 0,
                        }}
                      >
                        No UPC
                      </span>
                    ) : null}
                  </div>

                  {hasVariants && isExpanded ? (
                    <div
                      className="qv-promo-product-picker__variants"
                      style={{
                        marginLeft: 30,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        padding: "4px 0 8px",
                      }}
                    >
                      {visibleRows.map((row) => {
                        const key = getApiRowSelectionKey(row);
                        const selected = isSelected(row);
                        const noUpc = hasNoUpc(row);
                        const locked = noUpc && !selected;
                        // Stays clickable so the click can explain the lock.
                        const muted = locked || isZeroPriceLocked(row);
                        const label = formatVariantLabel(
                          row,
                          group.productName,
                        );
                        return (
                          <button
                            className="qv-promo-product-picker__variant"
                            key={key}
                            type="button"
                            onClick={() => toggleRow(row, group)}
                            disabled={listRefreshing || locked}
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "20px minmax(0, 1fr) auto auto",
                              gap: 12,
                              alignItems: "center",
                              padding: "8px 12px",
                              minWidth: 0,
                              overflow: "hidden",
                              background: muted
                                ? "var(--qv-bg-fafbfc, #fafbfc)"
                                : selected
                                  ? "var(--qv-brand-soft)"
                                  : "var(--qv-bg-ffffff, #ffffff)",
                              border: `1px solid ${muted
                                ? "var(--qv-bd-e3e6ea, #e3e6ea)"
                                : selected
                                  ? "var(--qv-brand-ring)"
                                  : "var(--qv-bd-eef0f3, #eef0f3)"
                                }`,
                              borderRadius: 8,
                              cursor:
                                listRefreshing || muted
                                  ? "not-allowed"
                                  : "pointer",
                              fontFamily: "inherit",
                              textAlign: "left",
                              opacity: listRefreshing || muted ? 0.6 : 1,
                            }}
                          >
                            <PickerCheckbox
                              state={selected ? "checked" : "unchecked"}
                            />
                            <span
                              style={{
                                minWidth: 0,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                overflow: "hidden",
                              }}
                            >
                              <EllipsisTooltip
                                inline
                                title={label || "Default"}
                                measureKey={`bogo-picker-variant-${key}`}
                                style={{
                                  flex: 1,
                                  fontSize: 12.5,
                                  fontWeight: 500,
                                  fontFamily: "CircularMedium",
                                  color: "var(--qv-tx-0f1115, #0f1115)",
                                }}
                              >
                                {label || "Default"}
                              </EllipsisTooltip>
                              <span
                                style={{
                                  flexShrink: 0,
                                  fontSize: 11,
                                  color: "var(--qv-tx-7a818f, #7a818f)",
                                  fontFamily: "ui-monospace, monospace",
                                }}
                              >
                                {row.upc || "-"}
                              </span>
                            </span>
                            <span
                              style={{
                                fontSize: 11.5,
                                color: "var(--qv-tx-7a818f, #7a818f)",
                                flexShrink: 0,
                                whiteSpace: "nowrap",
                              }}
                            >
                              $ {getRowPrice(row).toFixed(2)}
                            </span>
                            {noUpc ? (
                              <span
                                style={{
                                  fontSize: 10.5,
                                  padding: "2px 7px",
                                  borderRadius: 99,
                                  background: "var(--qv-bg-fdecea, #fdecea)",
                                  color: "var(--qv-tx-d32f2f, #d32f2f)",
                                  fontWeight: 500,
                                  fontFamily: "CircularMedium",
                                  letterSpacing: 0.3,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                NO UPC
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </section>
              );
            })
          )}
          {loadingMore ? (
            <div
              style={{
                padding: "12px 0",
                textAlign: "center",
                fontSize: 12,
                color: "var(--qv-tx-7a818f, #7a818f)",
              }}
            >
              Loading more...
            </div>
          ) : null}
        </main>

        <footer
          className="qv-promo-product-picker__footer"
          style={{
            padding: isNarrow ? "12px 16px" : "14px 24px",
            borderTop: "1px solid var(--qv-bd-eef0f3, #eef0f3)",
            display: "flex",
            flexDirection: isNarrow ? "column" : "row",
            alignItems: isNarrow ? "stretch" : "center",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <div
            className="qv-promo-product-picker__selection-status"
            style={{
              flex: isNarrow ? undefined : 1,
              minWidth: isNarrow ? undefined : 160,
              fontSize: 12.5,
              color: modalError ? "var(--qv-tx-d32f2f, #d32f2f)" : "var(--qv-tx-3d434f, #3d434f)",
            }}
            aria-live="polite"
          >
            {modalError ? (
              modalError
            ) : selectionCount > 0 ? (
              <>
                <b style={{ color: "var(--qv-tx-0f1115, #0f1115)" }}>{selectionCount}</b> product
                {selectionCount === 1 ? "" : "s"} selected
              </>
            ) : listRefreshing ? (
              <span style={{ color: "var(--qv-tx-7a818f, #7a818f)" }}>
                Waiting for the latest product list…
              </span>
            ) : (
              <span style={{ color: "var(--qv-tx-7a818f, #7a818f)" }}>
                Click products or variants to include them
              </span>
            )}
          </div>
          <div
            className="qv-promo-product-picker__footer-actions"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 10,
              flexShrink: 0,
              marginLeft: isNarrow ? undefined : "auto",
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              style={{ ...btnGhost, whiteSpace: "nowrap" }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectionCount === 0 || listRefreshing}
              style={{
                ...(selectionCount === 0 || listRefreshing
                  ? btnPrimaryDisabled
                  : btnPrimary),
                whiteSpace: "nowrap",
              }}
            >
              Update selection
            </button>
          </div>
        </footer>
      </div>
    </Modal>
  );
};

export default withReactQueryBoundary(React.memo(BogoProductSelectionModal));
